import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    Send as SendIcon,
    Inventory as ReceiveIcon,
    Cancel as CancelIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import purchaseOrderService from '../../../services/purchaseOrderService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import { useDataTable } from '../../../hooks/useDataTable';
import { useCurrency } from '../../../hooks/useCurrency';

const STATUS_COLORS = {
    DRAFT: 'default',
    SENT: 'info',
    RECEIVED: 'success',
    CANCELLED: 'error',
};

const formatDate = (dateString) => {
    if (!dateString) return '-';

    // DATE ONLY (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return new Date(dateString + 'T00:00:00').toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    // DATE + TIME
    return new Date(dateString).toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const PurchaseOrdersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { format } = useCurrency();

    // Page, size, search and sort live in the URL (/purchase-orders?page=0&size=20&search=...).
    const table = useDataTable(purchaseOrderService.getPurchaseOrdersPage, {}, {
        onError: () => toast.error(t('purchaseOrders.fetchError')),
    });

    const runAction = async (fn, po) => {
        try {
            await fn(po.id);
            toast.success(t('purchaseOrders.statusSuccess'));
            table.refresh();
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.saveError'));
        }
    };

    const buildActions = (po) => [
        {
            key: 'view',
            icon: ViewIcon,
            title: t('common.details'),
            color: 'info',
            onClick: () => navigate(`/purchase-orders/${po.id}`),
        },
        {
            key: 'send',
            icon: SendIcon,
            title: t('purchaseOrders.updateStatus'),
            color: 'primary',
            hidden: po.status !== 'DRAFT',
            onClick: () => runAction(purchaseOrderService.sendPurchaseOrder, po),
        },
        {
            key: 'receive',
            icon: ReceiveIcon,
            title: t('purchaseOrders.status.RECEIVED'),
            color: 'success',
            hidden: !(po.status === 'DRAFT' || po.status === 'SENT'),
            onClick: () => runAction(purchaseOrderService.receivePurchaseOrder, po),
        },
        {
            key: 'cancel',
            icon: CancelIcon,
            title: t('purchaseOrders.status.CANCELLED'),
            color: 'error',
            hidden: po.status === 'RECEIVED' || po.status === 'CANCELLED',
            onClick: () => runAction(purchaseOrderService.cancelPurchaseOrder, po),
        },
    ];

    const columns = [
        {
            field: 'poNumber',
            headerName: t('purchaseOrders.poNumber'),
            width: 190,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 'inherit' }}>
                    {params.value}
                </Typography>
            ),
        },
        { field: 'supplierName', headerName: t('purchaseOrders.supplier'), flex: 1, minWidth: 160 },
        {
            field: 'status',
            headerName: t('common.status'),
            width: 130,
            cellRenderer: (params) => (
                <Chip label={t(`purchaseOrders.status.${params.value}`, params.value)} color={STATUS_COLORS[params.value] || 'default'} size="small" />
            ),
        },
        {
            field: 'totalAmount',
            headerName: t('purchaseOrders.totalAmount'),
            width: 160,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 'inherit' }}>
                    {format(params.value)}
                </Typography>
            ),
            tooltipValueGetter: (params) => format(params.value),
        },
        {
            field: 'expectedDeliveryDate',
            headerName: t('purchaseOrders.expectedDate'),
            width: 150,
            cellRenderer: (params) => formatDate(params.value),
            tooltipValueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'createdAt',
            headerName: t('common.createdAt'),
            width: 230,
            cellRenderer: (params) =>
                `${formatDate(params.value)}${params.data.createdByUserName ? ` · ${params.data.createdByUserName}` : ''}`,
            tooltipValueGetter: (params) =>
                `${formatDate(params.value)}${params.data.createdByUserName ? ` · ${params.data.createdByUserName}` : ''}`,
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 160,
            sortable: false,
            cellRenderer: (params) => <GridActionsCell actions={buildActions(params.data)} />,
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('purchaseOrders.title')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/purchase-orders/create')}
                >
                    {t('purchaseOrders.createPO')}
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('purchaseOrders.searchPlaceholder')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                        onRowClicked={(e) => {
                            if (!e.event?.target?.closest?.('button')) navigate(`/purchase-orders/${e.data.id}`);
                        }}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default PurchaseOrdersList;
